// Redstone types + the world adapter the engine reads/writes through (RED-1/2/4,
// M15.1). PURE (ENG-1): no three/DOM/audio. The host (boot) classifies blocks
// into `RedRole`s and maps the engine's writes back to block states.
// See docs/redstone-semantics.md — that document is the binding contract.

/** A mounting/face direction. */
export type Dir = 'north' | 'east' | 'south' | 'west' | 'up' | 'down';
/** A horizontal facing (repeater/comparator output, lever/torch wall side). */
export type Facing = 'north' | 'east' | 'south' | 'west';

export const HORIZ: readonly Facing[] = ['north', 'east', 'south', 'west'];
export const ALL_DIRS: readonly Dir[] = ['north', 'east', 'south', 'west', 'up', 'down'];

/** Unit offset for a direction (block coords). */
export const DIR_VEC: Record<Dir, readonly [number, number, number]> = {
  north: [0, 0, -1],
  south: [0, 0, 1],
  west: [-1, 0, 0],
  east: [1, 0, 0],
  up: [0, 1, 0],
  down: [0, -1, 0],
};

export const OPPOSITE: Record<Dir, Dir> = {
  north: 'south', south: 'north', west: 'east', east: 'west', up: 'down', down: 'up',
};

/** The two horizontal directions perpendicular to a facing (its left+right). */
export function sidesOf(f: Facing): readonly [Facing, Facing] {
  return f === 'north' || f === 'south' ? ['east', 'west'] : ['north', 'south'];
}

/** The redstone role a block plays. The host derives this from the block name +
 *  shape + state props; every non-redstone solid is `block`, everything else is
 *  `air` (transparent, non-conducting). */
export type RedRole =
  | { kind: 'air' }
  | { kind: 'block' }
  | { kind: 'wire'; power: number }
  | { kind: 'source' } // redstone_block: powers adjacent dust to 15 (weak to blocks)
  | { kind: 'lever'; on: boolean; attach: Dir }
  | { kind: 'button'; on: boolean; attach: Dir }
  | { kind: 'plate'; on: boolean }
  | { kind: 'torch'; lit: boolean; attach: Dir }
  | { kind: 'repeater'; facing: Facing; delay: number; powered: boolean; locked: boolean }
  | { kind: 'comparator'; facing: Facing; mode: 'compare' | 'subtract'; powered: boolean }
  // Observer: watches the block at `facing`; while pulsing it strong-powers the
  // block on its opposite side (its back) and powers adjacent dust there (RED-3).
  | { kind: 'observer'; facing: Dir; powered: boolean }
  | { kind: 'lamp'; lit: boolean };

/** What the engine reads from the world. */
export interface RedstoneAccess {
  roleAt(x: number, y: number, z: number): RedRole;
  /** An opaque full cube that conducts strong power + blocks dust routing. */
  isConductor(x: number, y: number, z: number): boolean;
  /** Container-fill signal 0..15 for a comparator reading (x,y,z) behind it
   *  (E-9); -1 when there is no container there. */
  containerSignal(x: number, y: number, z: number): number;
}

/** What the engine writes back (host maps to block-state changes + remesh). */
export interface RedstoneWorld extends RedstoneAccess {
  /** New dust power at a wire cell. */
  setWire(x: number, y: number, z: number, power: number): void;
  /** New `powered` flag for repeater/comparator/lever/button/plate. */
  setPowered(x: number, y: number, z: number, on: boolean): void;
  /** New `lit` flag for a torch or lamp. */
  setLit(x: number, y: number, z: number, lit: boolean): void;
}

export const cellKey = (x: number, y: number, z: number): string => `${x},${y},${z}`;
export const parseKey = (k: string): [number, number, number] =>
  k.split(',').map(Number) as [number, number, number];
